import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IMedicationAdherenceRecord } from "../../../../@types/Patient";
import { Table } from "../../../../components/Table";
import { useMedicationAdherence } from "../../../../hooks/queries/medications";
import {
  formatTimeOfDay,
  getLongFormattedDate,
  getShortFormattedDate,
} from "../../../../utils/dateFormatter";
import { ModalSkeleton } from "../MeasurementModal/components/ModalSkeleton";
import {
  HeaderContainer,
  Subtitle,
  Title,
} from "../MeasurementModal/MeasurementModal.styles";
import { buildMedicationAdherenceColumns } from "./medicationAdherenceColumns";
import {
  ModalContainer,
  TableContainer,
} from "./MedicationAdherenceModal.styles";

export type MedicationAdherenceModalProps = {
  patientMedicationId: string;
  medicationName?: string;
  // EN-5078: wards with EnablePatientAddMedication — their medications carry a
  // structured schedule, so each row shows the dose's clock time, and the
  // Dosage/Route columns are hidden.
  patientAddMedication?: boolean;
};

// The modal always shows the full history (no date filter bar): an explicit
// far-past lower bound, since the API defaults to the last 30 days; the server
// clamps it to the medication's start date anyway. No upper bound = today.
const ALL_HISTORY_FROM_DATE = "2000-01-01";

// One row per dose slot per day (EN-5078); a dose the reminder went out for but
// the form was never submitted shows as "-".
function formatMarkedAsTaken(record: IMedicationAdherenceRecord): string {
  if (!record.submitted) {
    return "-";
  }

  return record.markedAsTaken
    ? i18next.t("patientPage_adherence_yes")
    : i18next.t("patientPage_adherence_no");
}

export function MedicationAdherenceModal({
  patientMedicationId,
  medicationName,
  patientAddMedication = false,
}: MedicationAdherenceModalProps) {
  const { t } = useTranslation();

  const { adherence, isForbidden, isFetching, isRefetching } =
    useMedicationAdherence(patientMedicationId, ALL_HISTORY_FROM_DATE);

  const columns = useMemo(
    () => buildMedicationAdherenceColumns(patientAddMedication),
    [patientAddMedication]
  );

  const isLoading = isFetching || isRefetching;

  const records = adherence?.records ?? [];

  // Subtitle = first record until today. Records come newest first, so the
  // earliest is the last one; with no records fall back to the API's clamped
  // range start (the medication's start date).
  const rangeStart =
    records.length > 0 ? records[records.length - 1].date : adherence?.from;

  const subtitle = rangeStart
    ? t("patientPage_medication_adherenceSubtitle", {
        start: getShortFormattedDate(rangeStart),
        end: getShortFormattedDate(new Date().toISOString()),
      })
    : "";

  const tableData = useMemo(
    () =>
      records.map((record) => ({
        id: record.date,
        dosage: adherence?.dosage ?? "-",
        source: adherence?.source ?? "-",
        route: adherence?.route ?? "-",
        frequency: adherence?.frequency ?? "-",
        // The dose slot's own clock time — the time the medication is marked
        // against in the mobile app, not the medication's full times list.
        time: formatTimeOfDay(record.time),
        markedAsTaken: formatMarkedAsTaken(record),
        actionSent: getLongFormattedDate(record.actionSentAt, "-"),
        submitted: getLongFormattedDate(record.submittedAt, "-"),
      })),
    [records, adherence]
  );

  const emptyStateText = isForbidden
    ? t("patientPage_adherence_noAccess")
    : t("patientPage_adherence_none");

  if (isLoading) {
    return <ModalSkeleton />;
  }

  return (
    <ModalContainer>
      <HeaderContainer style={{ marginBottom: 0 }}>
        <Title>
          {adherence?.name ??
            medicationName ??
            t("patientPage_adherence_fallbackTitle")}
        </Title>
        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
      </HeaderContainer>

      <TableContainer>
        <Table
          hasBorder={false}
          data={tableData}
          columns={columns}
          emptyStateText={emptyStateText}
        />
      </TableContainer>
    </ModalContainer>
  );
}
